import React, { useState } from "react";
import { TbPlayerPlay, TbTrash, TbPlus, TbFileText, TbPresentation, TbSend, TbLoader2 } from "react-icons/tb";
import { useProspectScan, useProspectHistory, useEmailTemplates } from "../../hooks/useProspecting";
import { useSort } from "../../hooks/useSort";
import { fmtDate } from "../../lib/dates";
import { ALL_GROUP_KEYS } from "../../lib/modules";
import { SUBJECT_TOKENS, BODY_TOKENS } from "../../lib/prospect";
import { Button, Field, Alert, EmptyState, SkeletonRows, SortHeader, Modal, ConfirmModal } from "../../components";
import { ModulePicker, ScanRunning } from "./ExternalRiskScan";
import ScanResults from "./ScanResults";
import SendProspectReportModal from "./SendProspectReportModal";
import generateSecurityPDF from "../../../components/pdfGenerator";
import { INTERNAL_REPORT_PREVIEW_KEY } from "../../../components/InternalReportPreview";
import PublicIpOptions from "../../../components/PublicIpOptions";
import "./Scanner.css";

// Prospecting mode: scan a domain without creating a customer organization, then send
// the report to the prospect. Three tabs, matching the existing feature.

// The token lists and the default wording live in lib/prospect now: the send dialog and
// the template editor both need them, and two copies is how they drift.

const TABS = [
  { key: "new", label: "New scan" },
  { key: "history", label: "History" },
  { key: "templates", label: "Templates" },
];

const ProspectingPanel = () => {
  const [tab, setTab] = useState("new");

  // The scan state comes from a module-level store (see useProspectScan), not from this
  // component, so a running scan survives both switching tabs here and leaving the
  // screen entirely. Previously either one destroyed `running` and `results`: the
  // request carried on and the server still filed the scan, but coming back showed an
  // empty form with no sign a scan had run and no route to the result.
  const scan = useProspectScan();
  const history = useProspectHistory();

  return (
    <>
      <div className="v2-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
                  className={`v2-tab${tab === t.key ? " on" : ""}`} onClick={() => setTab(t.key)}>
            {t.label}
            {/* A scan you walked away from is still running. Say so on the tab, or the
                only feedback is that nothing appears to be happening anywhere. */}
            {t.key === "new" && scan.running && <TbLoader2 size={13} className="v2-spin" style={{ marginLeft: ".375rem" }} />}
            {t.key === "new" && !scan.running && scan.results && tab !== "new" && <span className="v2-tab-dot" aria-label="New results" />}
          </button>
        ))}
      </div>
      {/* Mounted always, hidden when not selected: the scan form holds the domain, the
          module selection and the result, and none of that should evaporate because
          someone looked at their templates. */}
      <div hidden={tab !== "new"}>
        <NewProspectScan scan={scan} onDone={history.reload} />
      </div>
      {tab === "history" && <ProspectHistory history={history} />}
      {tab === "templates" && <ProspectTemplates />}
    </>
  );
};

const NewProspectScan = ({ scan, onDone }) => {
  const { run, clear, running, results, error, scanned } = scan;
  const [domain, setDomain] = useState("");
  const [publicIps, setPublicIps] = useState("");
  const [publicIpMode, setPublicIpMode] = useState("auto");
  // Captured at scan time, not only when the report is sent. Presenting a stack of these
  // meant reading domains to work out which company each report was for — the company is
  // known when the scan is started, so asking for it then costs one field and makes the
  // report, the history row and the PDF title all say who it is about.
  const [company, setCompany] = useState("");
  const [groups, setGroups] = useState(ALL_GROUP_KEYS);
  // No report theme here on purpose. Report style is stored on the scan and decides what
  // the prospect's own download page renders, so it has to be chosen before the scan runs.
  // The theme changes nothing about the scan and is applied when an output is generated —
  // asking for it twice just gave the same setting two homes that could disagree. It lives
  // on the report bar with the buttons it affects.
  const [reportStyle, setReportStyle] = useState("pdf");

  const allOn = groups.length === ALL_GROUP_KEYS.length;
  const toggle = (k) => setGroups((g) => (g.includes(k) ? g.filter((x) => x !== k) : [...g, k]));

  const canRun = Boolean(domain.trim() && groups.length && !running);
  const start = async () => {
    if (!canRun) return;
    const target = domain.trim();
    const data = await run({ domain: target, groups, reportStyle, company: company.trim(), publicIps, publicIpMode });
    if (!data) return;
    // The server files the scan the moment it finishes, so History is stale from here on
    // whether or not anyone is looking at it.
    onDone?.();
  };

  return (
    <>
      {/* Scanning only. Everything about sending the report moved behind the results,
          where the decision actually gets made. */}
      <div className="v2-scanform">
        <div className="v2-scanform-row">
          <Field label="Domain or public IPs" hint="One domain, or comma-separated public IPv4 addresses, ranges, or CIDRs. Up to 256 unique IPs.">
            <input className="v2-input" value={domain} onChange={(e) => setDomain(e.target.value)}
                   placeholder="example.com or 8.8.8.8, 1.1.1.1" maxLength={8192} disabled={running}
                   onKeyDown={(e) => { if (e.key === "Enter" && canRun) start(); }} />
          </Field>
          <Field label="Prospect company name (optional)" hint="Optional: helps report titles and automatic IP discovery match the prospect's registered company. Without it, discovery uses domain-based evidence.">
            <input className="v2-input" value={company} onChange={(e) => setCompany(e.target.value)}
                   placeholder="Example Company Ltd" aria-label="Prospect company name" maxLength={255} disabled={running}
                   onKeyDown={(e) => { if (e.key === "Enter" && canRun) start(); }} />
          </Field>
        </div>
        <div className="v2-scanform-row">
          <Field label="Report style" hint="How the prospect sees it. Changeable per scan.">
            <select className="v2-input" value={reportStyle} onChange={(e) => setReportStyle(e.target.value)} disabled={running}>
              <option value="pdf">PDF / single-page report</option>
              <option value="slide_deck">Slide deck presentation</option>
            </select>
          </Field>
        </div>

        <PublicIpOptions target={domain} enabled={groups.includes("infra")}
                         mode={publicIpMode} onModeChange={setPublicIpMode}
                         value={publicIps} onChange={setPublicIps} disabled={running} />

        <ModulePicker groups={groups} onToggle={toggle} allOn={allOn}
                      onToggleAll={() => setGroups(allOn ? [] : ALL_GROUP_KEYS)} disabled={running} />

        <Alert>{error}</Alert>

        <div className="v2-scanform-actions">
          <Button variant="primary" disabled={!canRun} onClick={start}>
            <TbPlayerPlay size={15} /> {running ? "Scanning…" : "Start prospecting scan"}
          </Button>
          <span className="v2-hint">
            Nothing is filed against a customer record, and nothing is emailed yet — you
            choose what to do with the report once it is back.
          </span>
        </div>
      </div>

      {running && <ScanRunning domain={domain} groups={groups} />}

      {results && !running && scanned && (
        <>
          <ReportActions scan={{ ...scanned, results }} onDismiss={clear} />
          <ScanResults results={results} domain={scanned.domain} />
        </>
      )}
    </>
  );
};

// What you can do with a finished report: keep it, show it, or send it.
//
// Light or dark for the generated report — the prospect's copy, not this interface.
const REPORT_THEMES = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const ReportThemePicker = ({ value, onChange, disabled }) => (
  <div className="v2-report-theme" role="group" aria-label="Report theme">
    <span className="v2-report-theme-label">Report</span>
    {REPORT_THEMES.map((t) => (
      <button key={t.value} type="button" disabled={disabled}
              aria-pressed={value === t.value}
              className={value === t.value ? "on" : ""}
              onClick={() => onChange(t.value)}>
        {t.label}
      </button>
    ))}
  </div>
);

// The same three actions the history rows offer, over the same props, so a report does
// not gain or lose options depending on whether you are looking at it now or next week.
const ReportActions = ({ scan, onSent, onDismiss }) => {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  // The report's own theme, not the application's.
  //
  // Every one of these outputs used to follow `isDarkMode`, so a salesperson working in
  // dark mode emailed prospects a dark PDF with no way to say otherwise — the app's
  // appearance silently decided what a customer received. Light is the default whatever
  // this screen looks like, because that is what a report being printed or forwarded
  // should be. It lives here rather than on the scan form because it changes nothing
  // about the scan, only how an output is rendered from it.
  const [reportTheme, setReportTheme] = useState("light");

  const downloadPdf = async () => {
    setBusy("pdf"); setError("");
    try {
      await generateSecurityPDF(scan.results, scan.domain, {
        theme: reportTheme,
        prospectName: scan.prospectName || "", prospectCompany: scan.prospectCompany || "",
        isCyberMode: false, saveFile: true,
        fileName: `security-report-${scan.prospectCompany || scan.domain}-${Date.now()}.pdf`,
      });
    } catch (e) {
      setError(e.message || "Could not generate the PDF.");
    } finally { setBusy(""); }
  };

  const openDeck = () => {
    setError("");
    try {
      sessionStorage.setItem(INTERNAL_REPORT_PREVIEW_KEY, JSON.stringify({
        results: { domain: scan.domain, ...scan.results },
        theme: reportTheme,
      }));
    } catch {
      // sessionStorage has a hard size cap and a big scan can exceed it.
      setError("This report is too large to open as a slide deck.");
      return;
    }
    // No 'noopener': sessionStorage is only copied into a tab opened by window.open
    // while it stays in the same browsing-context group, and that copy is the payload.
    window.open("/internal-report-preview", "_blank");
  };

  return (
    <>
      <div className="v2-reportbar">
        <span className="v2-section-title">{scan.prospectCompany || scan.domain} report</span>
        <div className="v2-reportbar-actions">
          {/* Applies to all three outputs below, so it sits with them rather than inside
              any one of them. */}
          <ReportThemePicker value={reportTheme} onChange={setReportTheme} />
          <Button onClick={downloadPdf} disabled={busy === "pdf"}>
            {busy === "pdf" ? <TbLoader2 size={15} className="v2-spin" /> : <TbFileText size={15} />}
            {busy === "pdf" ? "Building PDF…" : "Download PDF"}
          </Button>
          <Button onClick={openDeck}><TbPresentation size={15} /> Open slide deck</Button>
          <Button variant="primary" onClick={() => setSending(true)}><TbSend size={15} /> Email to prospect</Button>
          {onDismiss && <Button onClick={onDismiss} title="Clear these results">Dismiss</Button>}
        </div>
      </div>
      <Alert>{error}</Alert>

      {sending && (
        <SendProspectReportModal
          scan={scan}
          reportTheme={reportTheme}
          onClose={() => setSending(false)}
          onSent={onSent}
        />
      )}
    </>
  );
};

const ProspectHistory = ({ history }) => {
  const { items, loading, error, reload, remove } = history;
  // Sorting keys are the API's camelCase field names. They were snake_case here, which
  // matched nothing in the payload, so Score / Sent to / Scanned sorted on undefined.
  const { sort, toggleSort, sorted } = useSort(items, "createdAt", ["createdAt"]);
  const [open, setOpen] = useState(null);
  const [confirm, setConfirm] = useState(null);

  return (
    <>
      <Alert>{error}</Alert>
      <div className="v2-tablewrap">
        <table className="v2-table">
          <thead>
            <tr>
              {/* Company first: it is what someone scanning a list of past reports is
                  actually looking for. Domain stays, for the scans that have no company. */}
              <SortHeader label="Company" colKey="prospectCompany" sort={sort} onSort={toggleSort} />
              <SortHeader label="Domain" colKey="domain" sort={sort} onSort={toggleSort} />
              <SortHeader label="Score" colKey="riskScore" sort={sort} onSort={toggleSort} className="v2-num" />
              <SortHeader label="Findings" colKey="totalFindings" sort={sort} onSort={toggleSort} className="v2-num" />
              <SortHeader label="Sent to" colKey="prospectEmail" sort={sort} onSort={toggleSort} />
              <SortHeader label="Scanned" colKey="createdAt" sort={sort} onSort={toggleSort} />
              <th className="v2-num">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <SkeletonRows rows={4} cols={7} />
              : sorted.length === 0 ? (
                <tr><td colSpan={7}><EmptyState title="No prospecting scans yet">Run one from the New scan tab.</EmptyState></td></tr>
              ) : sorted.map((r) => (
                <React.Fragment key={r.scanId}>
                  <tr>
                    <td>{r.prospectCompany || <span className="v2-dim">—</span>}</td>
                    <td className="v2-mono">{r.domain || "—"}</td>
                    <td className="v2-num">{r.riskScore ?? "—"}</td>
                    <td className="v2-num v2-dim">{r.totalFindings ?? "—"}</td>
                    <td className="v2-dim v2-mono">{r.prospectEmail || "Not sent"}</td>
                    <td className="v2-dim">{fmtDate(r.createdAt)}</td>
                    <td className="v2-num">
                      {/* A row with no stored results cannot be rendered as anything —
                          the S3 payload is gone or failed to decrypt. Say so rather than
                          offering three buttons that would all fail. */}
                      {r.results && Object.keys(r.results).length > 0 ? (
                        <Button onClick={() => setOpen(open === r.scanId ? null : r.scanId)}>
                          {open === r.scanId ? "Hide" : "Report"}
                        </Button>
                      ) : <span className="v2-dim">Unavailable</span>}
                      <Button icon variant="danger" title="Delete scan" onClick={() => setConfirm(r)}>
                        <TbTrash size={15} />
                      </Button>
                    </td>
                  </tr>
                  {open === r.scanId && (
                    <tr>
                      <td colSpan={7} style={{ background: "var(--v2-surface)" }}>
                        <ReportActions scan={r} onSent={reload} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
          </tbody>
        </table>
      </div>

      {confirm && (
        <ConfirmModal
          title="Delete this scan?"
          body={`The ${confirm.domain} scan from ${fmtDate(confirm.createdAt)} will be removed permanently.`}
          onClose={() => setConfirm(null)}
          onConfirm={async () => { await remove(confirm.scanId); setConfirm(null); }}
        />
      )}
    </>
  );
};

const ProspectTemplates = () => {
  const { templates, loading, error, save, remove } = useEmailTemplates("prospect");
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  return (
    <>
      <div className="v2-pagehead" style={{ marginBottom: ".625rem" }}>
        <div />
        <div className="v2-pagehead-actions">
          <Button variant="primary" onClick={() => setEditing({ name: "", subject: "", body: "" })}>
            <TbPlus size={15} /> New template
          </Button>
        </div>
      </div>
      <Alert>{error}</Alert>
      {loading ? null : templates.length === 0 ? (
        <EmptyState title="No saved templates">Save one to reuse the same wording across prospects.</EmptyState>
      ) : (
        <div className="v2-tmpl-list">
          {templates.map((t) => (
            <div key={t.id} className="v2-tmpl">
              <div style={{ minWidth: 0 }}>
                <div className="v2-tmpl-name">{t.name}</div>
                <div className="v2-tmpl-subject">{t.subject}</div>
              </div>
              <Button onClick={() => setEditing(t)}>Edit</Button>
              <Button icon variant="danger" title="Delete" onClick={() => setConfirm(t)}><TbTrash size={15} /></Button>
            </div>
          ))}
        </div>
      )}

      {editing && <TemplateModal template={editing} onClose={() => setEditing(null)} onSave={save} />}
      {confirm && (
        <ConfirmModal title="Delete this template?" body={`"${confirm.name}" will be removed. This cannot be undone.`}
          onClose={() => setConfirm(null)}
          onConfirm={async () => { await remove(confirm.id); setConfirm(null); }} />
      )}
    </>
  );
};

// Matches the dialog the app already ships: a required name, then subject and body,
// each with its own placeholder line. The two sets differ — the body takes two more —
// and they are listed in full because whoever is writing a template reads this line to
// copy from.
const TemplateModal = ({ template, onClose, onSave }) => {
  const [form, setForm] = useState({ name: template.name || "", subject: template.subject || "", body: template.body || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { setErr("Give the template a name."); return; }
    setBusy(true); setErr("");
    try { await onSave({ id: template.id, ...form }); onClose(); }
    catch (e) { setErr(e.message || "Could not save the template."); }
    finally { setBusy(false); }
  };

  return (
    <Modal wide title={template.id ? "Edit prospect email template" : "Save prospect email template"} onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy || !form.name.trim()}>
          {busy ? "Saving…" : template.id ? "Update template" : "Save template"}
        </Button>
      </>}>
      <Alert>{err}</Alert>
      <Field label="Template name"><input className="v2-input" value={form.name} onChange={set("name")} placeholder="e.g., Standard Prospect Email" autoFocus /></Field>
      <Field label="Subject" hint={`Use ${SUBJECT_TOKENS} as placeholders`}>
        <input className="v2-input" value={form.subject} onChange={set("subject")} />
      </Field>
      <Field label="Body content" hint={`Use ${BODY_TOKENS}`}>
        <textarea className="v2-input" rows={10} value={form.body} onChange={set("body")} />
      </Field>
    </Modal>
  );
};

export default ProspectingPanel;
